import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TrackingService } from 'analytics';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LegalDocument } from 'shared';

/**
 * Wraps the /api/funnel/* endpoints. The session_token (JWT) is stored in
 * sessionStorage so a page reload during the quiz doesn't lose state.
 *
 * Backend contract: see backend/app/controllers/api/funnel/{leads,quiz,checkout}_controller.rb
 */

export type LeadStatus = 'email_sent' | 'redirect_login';

export interface LeadResponse {
  status:           LeadStatus;
  message?:         string;
  login_url?:       string;
  /** true when the email matches a user who already owns a FAC3LESS card */
  existing_account?: boolean;
}

export interface QuizOption {
  value:    string;
  i18n_key: string;
}

export interface QuizQuestion {
  key:       string;
  i18n_key:  string;
  /** Option-based questions carry an option list; free-text questions omit it and set input='text'. */
  options?:  QuizOption[];
  /** 'text' for a free-text (textarea) question; undefined for single-select. */
  input?:    'text';
  /** Minimum character count for free-text questions. */
  min?:      number;
  progress:  { current: number; total: number };
}

export interface QuizProduct {
  id:          number;
  code:        string;
  name:        string;
  price:       number | string;
  /**
   * Set only for a product whose `price` is not the whole price the customer is
   * quoted — today the fac3less card, whose activation fee is paid directly to the
   * card partner in the card dashboard and never charged here.
   */
  activation_fee?:       number | string | null;
  /** `price` + `activation_fee`; equals `price` when there is no fee. */
  total_customer_price?: number | string;
  description: string;
}

/**
 * A membership slot. A user may hold at most one of each at the same time: the
 * entry-tier Health Club (no underwriting) and one underwritten protection plan.
 */
export type MembershipCategory = 'club' | 'protection';

/**
 * The tier a club-slot membership is on: the plain WolfPack Membership, or the
 * Practitioner Network superset for health professionals. Both live in the same
 * `club` slot — the tier is derived from the plan code, not from a second slot.
 */
export type MembershipTier = 'club' | 'practitioner';

/** The rhythm a plan is billed in. 'week' plans are prepaid N weeks at a time. */
export type BillingPeriod = 'week' | 'month' | 'year';

/** A Global Health plan card shown on the result / plan-selection page. */
export interface QuizPlan {
  id:          number;
  code:        string;
  name:        string;
  /** Which slot this plan belongs to (club vs protection). */
  category:    MembershipCategory;
  /**
   * Which tier inside the club slot this plan belongs to — drives the grouping of
   * the plan cards. Optional: a backend that predates the Practitioner Network
   * omits it, and everything then reads as the plain club tier.
   */
  tier?:       MembershipTier;
  price:       number | string;
  /** 'week' -> the customer chooses how many weeks to prepay; otherwise fixed term. */
  billing_period?: BillingPeriod;
  photo_url:   string | null;
  recommended: boolean;
  /**
   * Website V2: a DNA & Health bundle (one year of membership + DNA kit + nutrition
   * plan) rather than a plain membership rhythm. Only listed when the wizard asked
   * for it with ?plan=… — never part of the default picker.
   */
  bundle?:     boolean;
  /** The bundle carries the satisfaction guarantee (DNA & Health SPECIAL). */
  guarantee?:  boolean;
  /** The DNA Family SPECIAL: a bundle without guarantee, invitation only. */
  family?:     boolean;
  /** The product this plan is a duration option of (null = other/retired code). */
  product_key?: 'membership' | 'practitioner' | 'dna_health_special' | 'dna_family_special' | null;
}

/**
 * What a paid DNA & Health bundle still owes the member (Website V2) — the
 * backend's HealthBundleFulfillment#member_json.
 */
export interface BundleFulfillment {
  product_code:          string;
  dna_kit_status:        'pending' | 'kit_shipped' | 'kit_delivered' | 'sample_received' |
                         'analysis_in_progress' | 'analysis_complete' | string;
  nutrition_plan_status: 'pending' | 'delivered' | string;
  nutrition_plan_delivered_at?: string | null;
  fitness_plan_status?:  'pending' | 'delivered' | string;
  fitness_plan_delivered_at?: string | null;
  /** The website journey and the goal the member chose (personalisation, not product). */
  customer_journey?:     string | null;
  customer_goal?:        string | null;
  ship_after?:           string | null;
  shipped_at?:           string | null;
  tracking_number?:      string | null;
  /** The kit's shipping address, or null while the member has not entered one. */
  shipping_address?:     BundleShippingAddress | null;
  /** The member's next required task: the address is still missing (kit not shipped). */
  shipping_address_required?: boolean;
  /** The address may still be entered or changed (kit not shipped, not withdrawn). */
  shipping_address_editable?: boolean;
  /** Legacy name of nutrition_plan_delivered_at. */
  delivered_at?:         string | null;
  /** The guarantee's payout amount — null when this bundle carries NO guarantee
   *  (decision E of 2026-10-03: a discounted / gifted / manually booked SPECIAL may
   *  have none or a custom one). Show the guarantee only when this is set. */
  guarantee_amount?:     string | null;
  /** SPECIAL only: confirmed receipt of the full payment + 365 days — the first day
   *  the guarantee can be claimed. */
  guarantee_deadline?:   string | null;
  /** End of the claim window (deadline + 6 weeks), or null only when the backend
   *  runs it open-ended (GH_GUARANTEE_CLAIM_WINDOW_DAYS=0). */
  guarantee_window_end?: string | null;
  guarantee_claimed_at?: string | null;
  /** True while the member may claim the satisfaction guarantee (once). */
  guarantee_claim_open:  boolean;
  /** Payout chosen with the claim. The details never come back from the server. */
  guarantee_payout_method?: GuaranteePayoutMethod | null;
  /** Set by the office once the guarantee has been paid out. */
  guarantee_paid_at?: string | null;
  /** Maximum days between a valid claim and the payout (30). */
  guarantee_payout_max_days?: number | null;
}

/** How the satisfaction guarantee is paid out (chosen by the member with the claim). */
export type GuaranteePayoutMethod = 'crypto' | 'bank_transfer';

/**
 * The shipping address of the DNA kit (customer briefing 2026-11-02 §3): the
 * member's first task after the purchase, worldwide — the country is one field
 * of the address, never a delivery check. Name, street, city and country are
 * required; postcode, state and phone are optional.
 */
export interface BundleShippingAddress {
  shipping_name:      string | null;
  shipping_address:   string | null;
  shipping_address2?: string | null;
  shipping_city:      string | null;
  shipping_postcode?: string | null;
  shipping_state?:    string | null;
  /** ISO 3166-1 alpha-2 */
  shipping_country:   string | null;
  shipping_phone?:    string | null;
  complete?:          boolean;
  confirmed_at?:      string | null;
}

/**
 * The difference-price offer that turns a paid, running Health Club membership
 * into a Practitioner Network one. Server-computed and server-enforced: the same
 * package (rhythm + number of weeks) the member already paid for, priced at the
 * difference, with the coverage dates left untouched. Null whenever the member is
 * not eligible (no paid order, already practitioner, still inside the
 * cancellation window, …) — the client never decides eligibility.
 */
export interface UpgradeOffer {
  /** The upgrade product the order is placed with (never a `selected_plan`). */
  product_code:   string;
  from_plan:      string;
  to_plan:        string;
  /** Weeks for a weekly package, 1 for a yearly one. */
  quantity:       number;
  billing_period: BillingPeriod;
  unit_price:     number;
  total_price:    number;
  currency:       string;
}

/**
 * The upgrade of an ACTIVE, self-paid YEARLY membership (Health Club or Practitioner)
 * to the DNA & Health SPECIAL — $2,099, i.e. the $2,499 SPECIAL with a flat $400
 * credit (customer decision 2026-10-03, D). Server-computed and server-enforced;
 * null when not eligible. The SPECIAL's 12 months start fresh on confirmed payment.
 */
export interface SpecialUpgradeOffer {
  product_code:     string;
  from_plan:        string;
  to_plan:          string;
  quantity:         number;
  unit_price:       number | string;
  total_price:      number | string;
  /** The SPECIAL's regular price. */
  regular_price?:   number | string | null;
  /** The credit for the running yearly membership. */
  credit:           number | string;
  /** The full standard satisfaction guarantee. */
  guarantee_amount: number | string;
  /** A Practitioner stays one after the upgrade. */
  practitioner:     boolean;
  currency:         string;
}

/**
 * The Practitioner Membership ANY member on a running DNA & Health SPECIAL may buy
 * SEPARATELY (customer decisions 2026-10-04). It sets or extends only the
 * Practitioner status (stacked on `retained_until` while that is ahead, else from
 * the payment) — never the SPECIAL's term or plan. Server-computed; null when not
 * eligible. Bought via POST …/application/practitioner_renewal (the "renewal" name
 * is historic: for a member who never was a Practitioner it is a first purchase).
 */
export interface PractitionerRenewalOffer {
  plans: { code: string; unit_price: number | string; currency: string; billing_period: 'week' | 'year' }[];
  /** Current end of the Practitioner status (may lie in the past); null = never a Practitioner. */
  retained_until?: string | null;
  retained:        boolean;
  /** The date the new term is added to. */
  base_date:       string;
  /** The SPECIAL's own end — unchanged by this purchase. */
  special_paid_through?: string | null;
  min_weeks:       number;
  max_weeks:       number;
}

/**
 * Special conditions of one of the member's MANUAL bookings (guarantee none /
 * custom, a discounted SPECIAL, or an immediate start) that the member has to
 * confirm — decision 7 and the shipping rule of 2026-10-04. Never blocks the
 * membership; an unconfirmed immediate start only keeps the kit until day 15.
 */
export interface BookingTerms {
  booking_id:        number;
  product_code:      string;
  regular_price:     string;
  charged_price:     string;
  discount_amount:   string;
  discounted:        boolean;
  currency:          string;
  guarantee_mode?:   'none' | 'standard' | 'custom' | null;
  guarantee_amount?: string | null;
  standard_guarantee_amount: string;
  /** The booking asks for an immediate start: confirming it releases the DNA kit inside the 14 days. */
  immediate_start?:  boolean;
  payment_status:    string;
  paid_at?:          string | null;
  required:          boolean;
  acknowledged:      boolean;
  acknowledged_at?:  string | null;
  terms_version:     string;
}

export type QuizStatus = 'started' | 'completed' | 'purchased' | 'abandoned';

export interface QuizSession {
  session_id:                 number;
  status:                     QuizStatus;
  answers:                    Record<string, string>;
  session_token?:             string;
  next_question:              QuizQuestion | null;
  recommended_product_code?:  string;
  product?:                   QuizProduct;
  /** Global Health only: all three plans, ordered, with the quiz pick flagged. */
  plans?:                     QuizPlan[];
  /** Global Health only: true when the funnel user already has a password (existing account). */
  account_exists?:            boolean;
}

export interface CheckoutResponse {
  order_id:         number;
  status:           string;
  payment_method:   string;
  total_price:      number | string;
  total_with_costs: number | string;
  product:          { code: string; name: string };
}

export interface PasswordSetupResponse {
  status: string;
  /** Address the password-setup link was emailed to (the funnel user's own). */
  email?: string;
}

// ----- Membership purchase wizard (Global Health) -------------------------

export interface MembershipCustomerInfo {
  first_name?:      string | null;
  last_name?:       string | null;
  date_of_birth?:   string | null;
  phone?:           string | null;
  ssn?:             string | null;
  res_address?:     string | null;
  res_city?:        string | null;
  res_state?:       string | null;
  res_postal_code?: string | null;
  res_country?:     string | null;
  billing_same_as_residential?: boolean;
  bill_address?:     string | null;
  bill_city?:        string | null;
  bill_state?:       string | null;
  bill_postal_code?: string | null;
  bill_country?:     string | null;
}

export interface PendingOrder {
  order_id:                 number;
  payment_method:           string;
  status:                   string;
  total_price:              number | string;
  total_with_costs:         number | string;
  currency?:                string | null;
  bank_transfer_reference?: string | null;
  /** False when picking another payment method silently replaces this order. */
  blocks_method_change?:    boolean;
  /** False for a web3 payment already on-chain — only support can resolve it. */
  can_cancel?:              boolean;
}

export interface MembershipApplication {
  id:                 number;
  application_status: string;
  membership_status:  string;
  commission_status:  string;
  /** The slot this membership occupies (club vs protection). */
  plan_category?:     MembershipCategory;
  /** Which tier the selected plan puts this membership on (club vs practitioner). */
  tier?:              MembershipTier;
  /** The product of the selected plan (membership / practitioner / SPECIAL), or null. */
  product_key?:       'membership' | 'practitioner' | 'dna_health_special' | 'dna_family_special' | null;
  /**
   * True when this membership was actually paid for with a club order (as opposed
   * to an admin-fee conversion or a $0 gift). Only a paid membership can be
   * upgraded at the difference price — which is why the server reports it.
   */
  paid_membership?:   boolean;
  /**
   * The plan codes the member may pick right now, enforced server-side by
   * `set_plan`. Absent on a backend that predates the Practitioner Network, in
   * which case every plan of the category is offered (the old behaviour).
   */
  offered_plan_codes?: string[];
  /**
   * Website V2: the DNA & Health bundles this membership may buy. A separate list
   * because a bundle is never part of the default offer; the server's set_plan and
   * checkout accept the union of both lists.
   */
  offered_bundle_codes?: string[];
  /**
   * DNA Family SPECIAL: the family bundle codes this member may buy — only with an
   * accepted, unexpired family invitation. Empty/absent = not purchasable.
   */
  offered_family_codes?: string[];
  /** The member's accepted family invitation (what the family purchase rides on), or null. */
  family_invitation?: { status: string; expires_at?: string | null } | null;
  /** The fulfilment of the latest bundle bought on this membership, or null. */
  bundle?:            BundleFulfillment | null;
  /** The Practitioner Network difference-price offer, or null when not eligible. */
  upgrade_offer?:     UpgradeOffer | null;
  /** The $2,099 upgrade to the DNA & Health SPECIAL, or null when not eligible. */
  special_upgrade_offer?: SpecialUpgradeOffer | null;
  /** A Practitioner who upgraded to the SPECIAL keeps the status until this date
   *  (the end of the Practitioner term already paid — decision 3 of 2026-10-04). */
  practitioner_retained_until?: string | null;
  /** The Practitioner Membership on a running SPECIAL, or null. */
  practitioner_renewal_offer?: PractitionerRenewalOffer | null;
  /** Special conditions of the member's manual bookings to confirm (decision 7). */
  booking_terms?: BookingTerms[];
  /**
   * True when the way into the Practitioner Network for this membership is the
   * REGULAR price rather than the difference: it was never paid for (admin-fee
   * conversion, $0 gift) or it was paid on a retired rhythm, so no `upgrade_offer`
   * exists. What the member gets in return is time — the term they buy is added on
   * top of `paid_through`, so nothing they already hold is lost.
   */
  practitioner_join_available?: boolean;
  /**
   * The date a further purchase stacks on (the running coverage end, or now once it
   * has lapsed), as the backend's renewal service computes it. Null when nothing is
   * being extended. Display only — the server's own date is what gets written.
   */
  renewal_base_date?: string | null;
  recommended_plan?:  string;
  selected_plan?:     string;
  selected_plan_differs_from_recommendation: boolean;
  /** False for the Health Club plans (no health declaration — skip straight to payment). */
  requires_underwriting?: boolean;
  /** Prepaid weeks (weekly plan only) plus the bookable range, straight from the backend. */
  term_weeks?:        number | null;
  min_weeks?:         number;
  max_weeks?:         number;
  start_mode?:        string | null;
  order_id?:          number | null;
  /** The member's own e-wallet balance in the selected plan's currency. */
  ewallet_balance?:   string | number | null;
  ewallet_currency?:  string | null;
  /** An order started but not yet completed (e.g. a bank transfer awaiting manual booking). */
  pending_order?:     PendingOrder | null;
  /** True once the application is finalized (declaration submitted/approved or purchased) — read-only. */
  locked?:            boolean;
  customer_info:      MembershipCustomerInfo;
  underwriting_status?: string | null;
  // Post-purchase (Step 15)
  purchase_date?:         string | null;
  coverage_start_date?:   string | null;
  coverage_end_date?:     string | null;
  /** Paid coverage end. Stored server-side; every renewal pushes it forward. */
  paid_through?:          string | null;
  expired?:               boolean;
  /** A further purchase EXTENDS this membership instead of creating a second one. */
  renewable?:             boolean;
  cancellation_deadline?: string | null;
  /** True while the 14-day cancellation window is still open. */
  can_cancel?:            boolean;
  documents?:             { doc_type: string; generated_at: string }[];
  plans:              QuizPlan[];
}

/** Both membership slots for the current user (either may be null when empty). */
export interface Memberships {
  club:       MembershipApplication | null;
  protection: MembershipApplication | null;
}

/**
 * The dashboard read model: the two slots plus every purchasable plan (the five
 * cards). `plans` lets the dashboard offer the full choice before anything is
 * selected — the wizard is what actually creates an application.
 */
export interface MembershipsResult {
  memberships: Memberships;
  plans:       QuizPlan[];
  /** The Practitioner team's mailbox for questions about the SPECIAL (per brand). */
  contact_email?: string | null;
}

export interface HealthDeclarationQuestion {
  key:  string;
  text: string;
  /** 'yes_no' or 'yes_no_na' (pregnancy adds a "Not applicable" option). */
  type: string;
  /** Conditional free-text field shown when the answer is "yes". */
  follow_up?: { key: string; label: string };
}

export interface DeclarationState {
  notice_accepted:      boolean;
  questions:            HealthDeclarationQuestion[];
  answers:              Record<string, string>;
  underwriting_status?: string | null;
  underwriting_score?:  number | null;
}

export interface DeclarationResult {
  underwriting_status: string;
  underwriting_score:  number;
  checkout_allowed:    boolean;
}

export interface MembershipCheckout {
  order_id:         number;
  status:           string;
  payment_method:   string;
  total_price:      number | string;
  total_with_costs: number | string;
  start_mode:       string;
  application_id:   number;
  currency?:        string | null;
  bank_transfer_reference?: string | null;
  /** Set by the Practitioner Network upgrade endpoint — same shape otherwise. */
  upgrade?:         boolean;
  /** Set by the DNA & Health SPECIAL upgrade endpoint — same shape otherwise. */
  special_upgrade?: boolean;
  /** Set by the Practitioner-Membership-on-SPECIAL endpoint — same shape otherwise. */
  practitioner_renewal?: boolean;
}

// ----- Direct checkout (no registration before the purchase) --------------

/** The documents a buyer accepts on the direct-checkout screen. */
export interface CheckoutLegalResponse {
  documents: LegalDocument[];
}

export interface CheckoutStartRequest {
  first_name:    string;
  last_name:     string;
  email:         string;
  language:      string;
  sponsor_id:    string | null;
  /** The website plan slug (weekly, yearly, practitioner_weekly, …). */
  plan:          string;
  weeks:         number | null;
  journey:       string | null;
  goal:          string | null;
  captcha_token: string | null;
  consents:      { key: string; version: string }[];
  /** DNA Family SPECIAL: the invitation token (plan `dna_family_special` only). */
  family_token?: string | null;
}

/**
 * POST /funnel/membership/start. Always 200; with a `family_token` it reports the
 * outcome of binding the invitation EITHER as `family_status: 'accepted'` + the
 * wizard URL `family_next`, OR as `family_error` (the accept codes).
 */
export interface MembershipStartResponse {
  account_exists: boolean;
  first_name?:    string;
  family_status?: 'accepted';
  family_next?:   string;
  family_error?:  string;
}

/** Options of the wizard's membership checkout beyond the positional basics. */
export interface MembershipCheckoutOptions {
  /** DNA Family SPECIAL: the member ticked "no voluntary money-back guarantee".
   *  The server answers 422 family_terms_required without it. */
  familyTermsAccepted?: boolean;
}

export type CheckoutStartResponse =
  | { status: 'ok'; next: string; first_name?: string }
  | { status: 'existing_account'; has_password: boolean };

const SESSION_TOKEN_KEY = 'globalhealth.funnel.session_token';

@Injectable({ providedIn: 'root' })
export class FunnelService {
  private http = inject(HttpClient);
  private tracking = inject(TrackingService);

  // ----- Leads ------------------------------------------------------------

  createLead(input: {
    first_name:     string;
    last_name?:     string;
    email:          string;
    phone?:         string;
    country?:       string;
    sponsor_id?:    string | number | null;
    language?:      string;
    captcha_token?: string | null;
    /** Website V2 hand-over (all optional; the backend drops invalid values):
     *  the entry chosen on the site and the door the visitor came through. */
    plan?:          string | null;
    weeks?:         number | null;
    journey?:       string | null;
    goal?:          string | null;
  }): Observable<LeadResponse> {
    return this.http.post<LeadResponse>('/funnel/leads', input);
  }

  // ----- Membership funnel entry ------------------------------------------

  /**
   * Consumes the magic-link token of the "ONE membership" funnel: confirms the
   * e-mail and sets the soft-login `cid` cookie, so the wizard endpoints work
   * before the member has a password. Deliberately does NOT start a quiz session
   * (there is no quiz in this funnel).
   */
  startMembershipFunnel(
    magicToken: string, sponsorId?: string | null, familyToken?: string | null
  ): Observable<MembershipStartResponse> {
    return this.http.post<MembershipStartResponse>(
      '/funnel/membership/start', {
        token: magicToken,
        ...(sponsorId ? { sponsor_id: sponsorId } : {}),
        ...(familyToken ? { family_token: familyToken } : {}),
        // Tracking Engine V1: links the landing session to the account (optional,
        // the backend ignores an unknown id).
        tracking_session_id: this.tracking.sessionId
      }
    );
  }

  // ----- Direct checkout ----------------------------------------------------

  /** The legal documents the direct checkout asks the buyer to accept (public). */
  getCheckoutLegal(language: string): Observable<CheckoutLegalResponse> {
    return this.http.get<CheckoutLegalResponse>('/funnel/checkout/legal', { params: { language } });
  }

  /**
   * Starts a purchase without a prior registration: creates (or recognises) the
   * account from the buyer's name + e-mail and, for a new account, sets the soft
   * login `cid` cookie so the wizard can take the payment right away.
   */
  startCheckout(input: CheckoutStartRequest): Observable<CheckoutStartResponse> {
    // tracking_session_id: Tracking Engine V1 — links the landing session (?wps=)
    // to the account. Optional; the backend ignores an unknown id.
    return this.http.post<CheckoutStartResponse>('/funnel/checkout/start',
      { ...input, tracking_session_id: this.tracking.sessionId });
  }

  /** Re-sends the confirmation mail of an account created by the direct checkout. */
  resendAccountMail(): Observable<{ status: 'sent' | 'already_confirmed' }> {
    return this.http.post<{ status: 'sent' | 'already_confirmed' }>('/funnel/checkout/resend_account_mail', {});
  }

  /**
   * Sets the password for the soft-logged-in funnel user (resolved server-side
   * from the `cid` cookie that startMembershipFunnel issued). Returns the address
   * to log in with — the client otherwise only knows what was typed into the lead
   * form, which is not necessarily the account's address.
   */
  setMembershipPassword(password: string): Observable<{ status: string; email: string }> {
    return this.http.post<{ status: string; email: string }>(
      '/funnel/membership/set_password', { password }
    );
  }

  // ----- Quiz (retired Health Finder — kept for reactivation) --------------

  startQuiz(magicToken: string): Observable<QuizSession> {
    return this.http.post<QuizSession>('/funnel/quiz/start', { token: magicToken });
  }

  showQuiz(): Observable<QuizSession> {
    return this.http.get<QuizSession>('/funnel/quiz', { headers: this.sessionHeaders() });
  }

  answerQuiz(key: string, value: string): Observable<QuizSession> {
    return this.http.post<QuizSession>(
      '/funnel/quiz/answer',
      { key, value },
      { headers: this.sessionHeaders() }
    );
  }

  /**
   * Rewind the quiz to a previous question (1-based `step`): the backend drops
   * the answers from that step onwards and re-asks the question. Powers the
   * in-quiz back link and the browser back button.
   */
  rewindQuiz(step: number): Observable<QuizSession> {
    return this.http.post<QuizSession>(
      '/funnel/quiz/rewind',
      { step },
      { headers: this.sessionHeaders() }
    );
  }

  /**
   * Emails the soft-logged-in funnel user a password-setup / login link so they
   * can create their account without buying first (or to finish later if a
   * purchase failed). The backend resolves the user from the funnel session.
   */
  requestPasswordSetup(): Observable<PasswordSetupResponse> {
    return this.http.post<PasswordSetupResponse>(
      '/funnel/quiz/password_setup',
      {},
      { headers: this.sessionHeaders() }
    );
  }

  /**
   * WolfPack wizard ending: set the password inline for the soft-logged-in
   * funnel user (no purchase, no second email). Resolves the user from the
   * funnel session on the backend.
   */
  setPassword(password: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      '/funnel/quiz/set_password',
      { password },
      { headers: this.sessionHeaders() }
    );
  }

  // ----- Checkout ---------------------------------------------------------

  createCheckout(paymentMethod: string): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(
      '/funnel/quiz/checkout',
      { payment_method: paymentMethod },
      { headers: this.sessionHeaders() }
    );
  }

  // ----- Membership purchase wizard ---------------------------------------

  /**
   * Find or create the current user's membership application for the given slot
   * (auth via cid cookie). Defaults to the club slot — the only one on sale.
   * Every subsequent wizard call must pass the same category so it acts on the
   * right one of the (up to) two memberships.
   */
  createApplication(category: MembershipCategory = 'club', plan?: string | null): Observable<MembershipApplication> {
    // `plan` only matters for a DNA & Health bundle: the backend lists a bundle in
    // `plans` only when it is asked for by code (Website V2 sales pages).
    return this.http.post<MembershipApplication>('/funnel/membership/application',
                                                 plan ? { category, plan } : { category });
  }

  /**
   * Claim the satisfaction guarantee of the member's DNA & Health SPECIAL. Allowed
   * once, inside the claim window the server reports (`guarantee_claim_open`).
   * The payout choice is required: crypto (USDT/USDC on BSC, details = the 0x
   * wallet address) or a USD bank transfer (details = account, bank, holder).
   * 422 with `code` when it is missing or invalid.
   */
  /**
   * Records the member's shipping address for the DNA kit (first task after the
   * purchase). 422 `shipping_address_invalid` carries `fields`; 409
   * `shipping_address_locked` once the kit has left.
   */
  updateBundleShippingAddress(
    address: BundleShippingAddress, orderId?: number | null
  ): Observable<{ bundle: BundleFulfillment }> {
    return this.http.patch<{ bundle: BundleFulfillment }>('/funnel/membership/bundle/shipping_address', {
      ...address,
      ...(orderId ? { order_id: orderId } : {})
    });
  }

  claimBundleGuarantee(
    payoutMethod: GuaranteePayoutMethod, payoutDetails: string,
    note?: string | null, orderId?: number | null
  ): Observable<{ bundle: BundleFulfillment }> {
    return this.http.post<{ bundle: BundleFulfillment }>('/funnel/membership/bundle/guarantee_claim', {
      payout_method: payoutMethod,
      payout_details: payoutDetails,
      ...(note ? { note } : {}),
      ...(orderId ? { order_id: orderId } : {})
    });
  }

  /**
   * Read-only: both membership slots for the current user (each null when empty).
   * Never creates a row. The dashboard renders one card per slot, branching on
   * membership_status: PENDING_APPLICATION -> finalize widget; purchased -> status
   * card (Step 15); empty/canceled -> "add" prompt.
   */
  getMemberships(plan?: string | null): Observable<MembershipsResult> {
    // `plan` only lists a DNA & Health bundle in `plans` (the backend never lists
    // one unasked) — the dashboard asks for the SPECIAL to show its server price.
    return this.http.get<MembershipsResult>('/funnel/membership/application',
                                            plan ? { params: { plan } } : {});
  }

  /** DEV/STAGING dummy payment: completes the pending order without a real gateway. */
  devCompletePayment(category: MembershipCategory): Observable<MembershipApplication> {
    return this.http.post<MembershipApplication>('/funnel/membership/application/dev_complete_payment', { category });
  }

  /** Download a generated membership document (owner-scoped). */
  downloadMembershipDocument(docType: string, category: MembershipCategory): Observable<Blob> {
    return this.http.get(`/funnel/membership/documents/${docType}`, { params: { category }, responseType: 'blob' });
  }

  setApplicationPlan(plan: string, category: MembershipCategory): Observable<MembershipApplication> {
    return this.http.patch<MembershipApplication>('/funnel/membership/application/plan', { plan, category });
  }

  setCustomerInfo(info: MembershipCustomerInfo, category: MembershipCategory): Observable<MembershipApplication> {
    return this.http.patch<MembershipApplication>('/funnel/membership/application/customer_info', { ...info, category });
  }

  checkoutMembership(
    paymentMethod: string,
    startMode: 'standard' | 'immediate',
    waiverAccepted = false,
    category: MembershipCategory = 'club',
    /** Prepaid weeks — only read by the backend for the weekly plan. */
    weeks?: number | null,
    /** The newest referral this browser holds (last touch; the backend locks the
     *  sponsor only at the first confirmed full payment). */
    sponsorId?: string | null,
    options: MembershipCheckoutOptions = {}
  ): Observable<MembershipCheckout> {
    return this.http.post<MembershipCheckout>(
      '/funnel/membership/application/checkout',
      {
        payment_method: paymentMethod, start_mode: startMode, waiver_accepted: waiverAccepted, category, weeks,
        ...(sponsorId ? { sponsor_id: sponsorId } : {}),
        ...(options.familyTermsAccepted ? { family_terms_accepted: true } : {})
      }
    );
  }

  /**
   * Buy the Practitioner Network upgrade for an existing, paid club membership.
   * Everything about the package (rhythm, weeks, price) comes from the server's
   * `upgrade_offer` — the client sends only how to pay and the waiver, so the two
   * can never disagree. The response is the same order payload `checkout` returns
   * (plus `upgrade: true`), so the payment step handles both identically.
   *
   * The category travels as a query param (per the API contract) and in the body,
   * so it reaches the server whichever of the two the action reads.
   */
  upgradeMembership(
    paymentMethod: string,
    waiverAccepted: boolean,
    category: MembershipCategory = 'club',
    sponsorId?: string | null
  ): Observable<MembershipCheckout> {
    return this.http.post<MembershipCheckout>(
      '/funnel/membership/application/upgrade',
      {
        payment_method: paymentMethod, waiver_accepted: waiverAccepted, category,
        ...(sponsorId ? { sponsor_id: sponsorId } : {})
      },
      { params: { category } }
    );
  }

  /**
   * Buy the upgrade to the DNA & Health SPECIAL ($2,099) for an ACTIVE, self-paid
   * yearly membership. The price, the credit and the eligibility are the server's
   * (`special_upgrade_offer`). `startMode` 'immediate' (with the waiver) releases the
   * DNA kit at once; 'standard' after the 14-day window.
   */
  specialUpgradeMembership(
    paymentMethod: string,
    startMode: 'standard' | 'immediate',
    waiverAccepted: boolean,
    sponsorId?: string | null
  ): Observable<MembershipCheckout> {
    return this.http.post<MembershipCheckout>(
      '/funnel/membership/application/special_upgrade',
      {
        payment_method: paymentMethod, start_mode: startMode, waiver_accepted: waiverAccepted, category: 'club',
        ...(sponsorId ? { sponsor_id: sponsorId } : {})
      },
      { params: { category: 'club' } }
    );
  }

  /**
   * Buy the Practitioner Membership on a running DNA & Health SPECIAL (decision 3
   * of 2026-10-04): `plan` is one of `practitioner_renewal_offer.plans`, `weeks`
   * only for the weekly rhythm. Extends only the Practitioner status.
   */
  practitionerRenewal(
    plan: string,
    weeks: number | null,
    paymentMethod: string,
    sponsorId?: string | null
  ): Observable<MembershipCheckout> {
    return this.http.post<MembershipCheckout>(
      '/funnel/membership/application/practitioner_renewal',
      {
        plan, payment_method: paymentMethod, category: 'club',
        ...(weeks ? { weeks } : {}),
        ...(sponsorId ? { sponsor_id: sponsorId } : {})
      },
      { params: { category: 'club' } }
    );
  }

  /** The member confirms the special conditions of their manual booking. */
  acknowledgeBookingTerms(bookingId: number, termsVersion: string): Observable<{ terms: BookingTerms }> {
    return this.http.post<{ terms: BookingTerms }>(
      `/funnel/membership/bookings/${bookingId}/acknowledge`, { terms_version: termsVersion }
    );
  }

  cancelMembership(category: MembershipCategory): Observable<MembershipApplication> {
    return this.http.post<MembershipApplication>('/funnel/membership/application/cancel', { category });
  }

  /** Abandon a started-but-unpaid checkout (e.g. a bank transfer): cancels the
   *  order and returns the application so the wizard restarts at plan selection. */
  cancelCheckout(category: MembershipCategory): Observable<MembershipApplication> {
    return this.http.post<MembershipApplication>('/funnel/membership/application/cancel_checkout', { category });
  }

  // ----- Health Declaration (English only) --------------------------------

  getDeclaration(category: MembershipCategory): Observable<DeclarationState> {
    return this.http.get<DeclarationState>('/funnel/membership/declaration', { params: { category } });
  }

  acceptDeclarationNotice(category: MembershipCategory): Observable<{ notice_accepted: boolean }> {
    return this.http.post<{ notice_accepted: boolean }>('/funnel/membership/declaration/notice', { category });
  }

  submitDeclaration(answers: Record<string, string>, acks: {
    ack_truthful: boolean; ack_false_info: boolean; ack_underwriting: boolean;
  }, category: MembershipCategory): Observable<DeclarationResult> {
    return this.http.post<DeclarationResult>('/funnel/membership/declaration', { answers, ...acks, category });
  }

  requestManualReview(category: MembershipCategory): Observable<{ underwriting_status: string }> {
    return this.http.post<{ underwriting_status: string }>('/funnel/membership/declaration/request_review', { category });
  }

  // ----- Session token ----------------------------------------------------

  storeSessionToken(token: string): void {
    try { sessionStorage.setItem(SESSION_TOKEN_KEY, token); } catch { /* ignore */ }
  }

  getSessionToken(): string | null {
    try { return sessionStorage.getItem(SESSION_TOKEN_KEY); } catch { return null; }
  }

  clearSessionToken(): void {
    try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
  }

  private sessionHeaders(): HttpHeaders {
    const token = this.getSessionToken() ?? '';
    return new HttpHeaders({ 'X-Funnel-Session': token });
  }
}
